import { useEffect, useRef, useState } from 'react'
import { failureText, inbox } from './api'
import { label } from './inbox'
import type { AccountInfo } from './store'
import { Tile } from './visuals'
import { GhostTextarea } from './ghost'
import { Kbd } from './glyphs'
import { addresses, RecipientInput, validEmail } from './recipients'

const valid = validEmail

/** A calm document: From, To, Subject, then the message. Everything has a key. */
export function Compose({ accounts, defaultAccountId, onClose, onSent }: {
  accounts: AccountInfo[]; defaultAccountId: string | null
  onClose: () => void; onSent: (operationId: string, label: string) => void
}) {
  const [from, setFrom] = useState(() => defaultAccountId ?? accounts[0]?.id ?? '')
  const [to, setTo] = useState('')
  const [cc, setCc] = useState('')
  const [showCc, setShowCc] = useState(false)
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [error, setError] = useState('')
  const [sending, setSending] = useState(false)
  const toRef = useRef<HTMLInputElement>(null)
  useEffect(() => { toRef.current?.focus() }, [])

  async function send() {
    if (sending) return
    const account = accounts.find(item => item.id === from)
    const recipients = addresses(to), copies = addresses(cc)
    if (!account) { setError('Connect an account first.'); return }
    if (!recipients.length) { setError('Add at least one recipient.'); return }
    const bad = [...recipients, ...copies].find(r => !valid(r.email))
    if (bad) { setError(`“${bad.email}” isn’t an email address.`); return }
    setSending(true); setError('')
    try {
      const draft = await inbox.createDraft({ accountId: account.id, from: account.email, to: recipients, cc: copies, subject: subject.trim(), bodyText: body, mode: 'compose' })
      const operation = await inbox.submit(draft.id, { revision: draft.revision, idempotencyKey: crypto.randomUUID() })
      onSent(operation.id, 'Sent')
      onClose()
    } catch (failure) {
      setError(failureText(failure))
      setSending(false)
    }
  }

  const onKey = (event: React.KeyboardEvent) => {
    event.stopPropagation()
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) { event.preventDefault(); void send() }
    else if (event.key === 'Escape') { event.preventDefault(); onClose() }
    else if (event.key.toLowerCase() === 'j' && (event.metaKey || event.ctrlKey) && accounts.length > 1) {
      event.preventDefault()
      const index = accounts.findIndex(item => item.id === from)
      setFrom(accounts[(index + 1) % accounts.length]!.id)
    } else if (event.key.toLowerCase() === 'c' && (event.metaKey || event.ctrlKey) && event.shiftKey) { event.preventDefault(); setShowCc(true) }
  }

  return (
    <div className="compose" onKeyDown={onKey}>
      <div className="compose-col view-in">
        <div className="field">
          <span className="field-label">From</span>
          <div className="field-value from-picker">
            {accounts.map(account => (
              <button key={account.id} tabIndex={-1} className={`from-option${account.id === from ? ' on' : ''}`} onClick={() => setFrom(account.id)} type="button" title={account.email}>
                <Tile brand={account.brand} tone={account.tone} size={16} />{label(account, accounts)}
                {account.id === from && <span className="faint">{account.email}</span>}
              </button>
            ))}
          </div>
          {accounts.length > 1 && <span className="field-hint"><Kbd k="⌘J" /></span>}
        </div>
        <div className="field">
          <span className="field-label">To</span>
          <RecipientInput ref={toRef} className="field-input" value={to} onChange={setTo} />
          {!showCc && <button className="field-hint" tabIndex={-1} onClick={() => setShowCc(true)} type="button"><Kbd k="⌘⇧C" /> Cc</button>}
        </div>
        {showCc && <div className="field"><span className="field-label">Cc</span><RecipientInput className="field-input" value={cc} onChange={setCc} autoFocus /></div>}
        <div className="field">
          <input className="field-input subject-input" value={subject} onChange={event => setSubject(event.target.value)} placeholder="Subject" />
        </div>
        <GhostTextarea className="compose-body" value={body} onChange={setBody} placeholder="Write…" context={{ to, subject }} />
        {(error || sending) && <div className={`small ${error ? 'warn' : 'muted'}`}>{error || 'Sending…'}</div>}
      </div>
    </div>
  )
}
